import { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { TabId } from './types';
import { usePWA } from './hooks/usePWA';
import { useHaptic } from './hooks/useHaptic';
import { useLocalStorage } from './hooks/useLocalStorage';
import { BookCover } from './components/BookCover';
import { ThresholdPulse } from './components/ThresholdPulse';
import { Navigation } from './components/Navigation';
import { QuickActions } from './components/QuickActions';
import './styles/mobile.css';

// Lazy load tab content and modals for performance
const WritingPad = lazy(() => import('./components/WritingPad'));
const MobileChat = lazy(() => import('./components/MobileChat'));
const MemoryDrawer = lazy(() => import('./components/MemoryDrawer'));
const SettingsPanel = lazy(() => import('./components/SettingsPanel'));
const DecreeBuilder = lazy(() => import('./components/DecreeBuilder'));
const VoicePlayer = lazy(() => import('./components/VoicePlayer'));

const TabFallback = () => (
  <div className="tab-fallback">
    <div className="tab-fallback-orb" />
    <p>Crossing the threshold...</p>
  </div>
);

export default function App() {
  const [activeTab, setActiveTab] = useState<TabId>('threshold');
  const [showSettings, setShowSettings] = useState(false);
  const [showDecree, setShowDecree] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [firstVisit, setFirstVisit] = useLocalStorage('remme_first_visit', true);
  const { offline, canInstall, promptInstall, dismissInstall } = usePWA();
  const { trigger } = useHaptic();

  useEffect(() => {
    if (!firstVisit) {
      setBookOpen(true);
    }
  }, [firstVisit]);

  const handleTabChange = useCallback(
    (tab: TabId) => {
      trigger('light');
      setActiveTab(tab);
    },
    [trigger]
  );

  const handleQuickAction = useCallback(
    (action: string) => {
      trigger('medium');
      if (action === 'chat') setActiveTab('chat');
      if (action === 'write') setActiveTab('write');
      if (action === 'decree') setShowDecree(true);
      if (action === 'voice') setShowVoice(true);
    },
    [trigger]
  );

  return (
    <div className="app-container">
      {!bookOpen ? (
        <BookCover onOpen={() => { setBookOpen(true); setFirstVisit(false); }} />
      ) : (
        <>
          <header className="app-bar" role="banner">
            <div className="app-bar-title">Book of RēM</div>
            <div className="app-bar-actions">
              {offline && (
                <span className="offline-badge" aria-label="Offline">
                  The threshold is thin
                </span>
              )}
              <button
                className="app-bar-btn"
                aria-label="Voice player"
                onClick={() => setShowVoice(true)}
              >
                🎙️
              </button>
              <button
                className="app-bar-btn"
                aria-label="Settings"
                onClick={() => setShowSettings(true)}
              >
                ⚙️
              </button>
            </div>
          </header>

          {canInstall && (
            <div className="install-banner" role="alert">
              <span>Add Book of RēM to your home screen</span>
              <div className="install-banner-actions">
                <button onClick={promptInstall}>Install</button>
                <button onClick={dismissInstall}>Later</button>
              </div>
            </div>
          )}

          <main className="app-content" role="main">
            <Suspense fallback={<TabFallback />}>
              {activeTab === 'threshold' && (
                <ThresholdPulse onQuickAction={handleQuickAction} />
              )}
              {activeTab === 'write' && <WritingPad />}
              {activeTab === 'chat' && <MobileChat />}
              {activeTab === 'memory' && <MemoryDrawer open />}
            </Suspense>
          </main>

          <QuickActions onAction={handleQuickAction} />

          <Navigation activeTab={activeTab} onTabChange={handleTabChange} />

          <Suspense fallback={<TabFallback />}>
            {showSettings && (
              <SettingsPanel onClose={() => setShowSettings(false)} />
            )}
            {showDecree && (
              <DecreeBuilder onClose={() => setShowDecree(false)} />
            )}
            {showVoice && (
              <VoicePlayer onClose={() => setShowVoice(false)} />
            )}
          </Suspense>
        </>
      )}
    </div>
  );
}
